import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, X, CheckCircle, Mic, MicOff, Send, Loader2, Heart, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import { detectHarmContent, detectUnconstructiveContent, containsProfanity } from '../shared.jsx';
import AiContentReportButton from './AiContentReportButton.jsx';
import ModalDialog from './ModalDialog.jsx';

// ═══════════════════════════════════════════════════════════════
//  SAY IT YOUR WAY — Communication Phase
//  Inline chat panel for Because statement generation.
//  Props: isOpen, onClose, onExtracted(becauseStatement), conflictContext, previousStatements, trigger
// ═══════════════════════════════════════════════════════════════

const CLOUD_FUNCTION_URL = 'https://us-central1-argumentor-ff17a.cloudfunctions.net/sayItYourWayChat';

const GREETING = "Hey! 👋 I'm here to help you put your feelings into words. Just vent. Tell me what's bothering you about this situation. Don't worry about format or being polite. I'll help shape it into a proper statement when you're ready.\n\nSo, what's on your mind?";

// Emotional tone analysis (same as original SIYW)
const analyzeEmotionalTone = (messages) => {
  if (!messages.length) return { level: 0, label: "Neutral", color: "stone" };
  const userMessages = messages.filter((m) => m.role === "user");
  if (!userMessages.length) return { level: 0, label: "Neutral", color: "stone" };
  const recentMessages = userMessages.slice(-3);
  const text = recentMessages.map((m) => m.content).join(" ").toLowerCase();
  const highIntensity = [/!{2,}/, /\b(hate|furious|livid|enraged|disgusting|unbearable|worst|terrible|horrible|outrageous)\b/i, /\b(can't stand|sick of|fed up|had enough|last straw|done with)\b/i, /\b(always|never|every single time|constantly)\b/i, /[A-Z]{3,}/];
  const medIntensity = [/\b(angry|upset|frustrated|annoyed|irritated|bothered|stressed|anxious|worried|hurt|disappointed)\b/i, /\b(unfair|wrong|shouldn't|problem|issue|conflict|argument|fight|disagree)\b/i, /!$/];
  const lowIntensity = [/\b(think|feel|wonder|hope|wish|prefer|understand|see their point|maybe|perhaps)\b/i, /\b(want to resolve|work through|figure out|compromise|solution|better)\b/i];
  let score = 50;
  highIntensity.forEach((r) => { if (r.test(text)) score += 15; });
  medIntensity.forEach((r) => { if (r.test(text)) score += 8; });
  lowIntensity.forEach((r) => { if (r.test(text)) score -= 10; });
  if (userMessages.length > 2) {
    const early = userMessages.slice(0, 2).map((m) => m.content).join(" ");
    const late = userMessages.slice(-2).map((m) => m.content).join(" ");
    if ((late.match(/!/g) || []).length < (early.match(/!/g) || []).length) score -= 10;
    if ((late.match(/[A-Z]{3,}/g) || []).length < (early.match(/[A-Z]{3,}/g) || []).length) score -= 10;
  }
  score = Math.max(0, Math.min(100, score));
  if (score >= 75) return { level: score, label: "Very Heated", color: "red" };
  if (score >= 55) return { level: score, label: "Frustrated", color: "orange" };
  if (score >= 35) return { level: score, label: "Processing", color: "amber" };
  if (score >= 15) return { level: score, label: "Calming Down", color: "emerald" };
  return { level: score, label: "Composed", color: "green" };
};

const extractJSON = (text) => {
  const match = text.match(/```json\s*([\s\S]*?)\s*```/);
  if (match) { try { return JSON.parse(match[1]); } catch (e) { return null; } }
  return null;
};

const cleanDisplayText = (text) => text.replace(/```json[\s\S]*?```/, "").trim();

// Client-side output sanitization
const CLIENT_OUTPUT_PROFANITY = [
  'fuck', 'shit', 'damn', 'bitch', 'asshole', 'bastard', 'crap', 'piss', 'dick',
  'cock', 'pussy', 'slut', 'whore', 'cunt', 'twat', 'prick', 'dumbass', 'jackass',
  'bullshit', 'shitty', 'fucking', 'bitching', 'shite', 'bellend', 'tosser', 'pillock'
];
const CLIENT_OUTPUT_REPLACEMENTS = [
  [/\b(idiot|moron|loser|jerk|scumbag|trash|worthless)\b/gi, 'the other person'],
  [/\b(narcissis(?:t|tic)|sociopath(?:ic)?|psycho(?:path|pathic)?|toxic)\b/gi, 'harmful behavior'],
  [/\b(gasligh(?:t|ting|ted|ter))\b/gi, "dismissing my perspective"],
  [/\b(manipulat(?:ive|or|ing)|control\s*freak)\b/gi, 'controlling behavior'],
  [/\b(drunk(?:ard)?|alcoholic|lush|boozer)\b/gi, 'heavy drinking'],
  [/\b(junkie|druggie|pothead|stoner|crackhead)\b/gi, 'substance use'],
  [/\b(lost\s+(?:their|his|her)\s+(?:mind|shit|marbles))\b/gi, 'became very upset'],
  [/\b(went\s+(?:ballistic|berserk|postal|nuts|crazy))\b/gi, 'reacted with strong emotion'],
  [/\b(flip(?:s|ped|ping)?\s+out)\b/gi, 'reacted strongly'],
  [/\b(threw?\s+a\s+(?:fit|tantrum))\b/gi, 'had a strong emotional reaction'],
  [/\b(drama\s+queen|cry\s*baby|snowflake)\b/gi, 'someone who is upset'],
  [/\b(overreact(?:s|ing|ed)?)\b/gi, 'reacted strongly'],
];

const sanitizeBecauseOutput = (text) => {
  if (!text || typeof text !== 'string') return text;
  let cleaned = text;
  for (const word of CLIENT_OUTPUT_PROFANITY) {
    cleaned = cleaned.replace(new RegExp('\\b' + word + '\\b', 'gi'), '');
  }
  for (const [pattern, replacement] of CLIENT_OUTPUT_REPLACEMENTS) {
    cleaned = cleaned.replace(pattern, replacement);
  }
  return cleaned.replace(/\s{2,}/g, ' ').trim();
};

// Harm detection (client-side)
const HARM_PATTERNS = {
  selfHarm: [
    /\b(want(?:s|ing)?\s+to\s+(?:die|end\s+(?:it|my\s+life|everything)|kill\s+myself|not\s+(?:be\s+here|exist|live|wake\s+up)))\b/i,
    /\b(suicid(?:e|al)|self[-]?harm|cut(?:ting)?\s+my(?:self)?|hurt(?:ing)?\s+myself)\b/i,
    /\b(no\s+(?:reason|point)\s+(?:to|in)\s+(?:live|living|go(?:ing)?\s+on))\b/i,
    /\b(better\s+off\s+(?:dead|without\s+me))\b/i,
    /\b(can'?t\s+(?:take|do)\s+(?:it|this)\s+anymore)\b/i,
  ],
  harmToOthers: [
    /\b((?:going\s+to|gonna|want\s+to|plan(?:ning)?\s+to)\s+(?:kill|hurt|attack|stab|shoot|poison))\b/i,
    /\b((?:they|he|she)\s+(?:deserve(?:s)?\s+to\s+die|should\s+(?:die|be\s+killed)))\b/i,
    /\b(bring(?:ing)?\s+(?:a\s+)?(?:gun|knife|weapon))\b/i,
  ],
};

const detectHarmClientSide = (text) => {
  if (!text || text.trim().length < 5) return { detected: false, type: null };
  const cleaned = text.replace(/[*_~`#]/g, '').trim();
  let selfHarm = false, harmOthers = false;
  for (const p of HARM_PATTERNS.selfHarm) { if (p.test(cleaned)) { selfHarm = true; break; } }
  for (const p of HARM_PATTERNS.harmToOthers) { if (p.test(cleaned)) { harmOthers = true; break; } }
  if (!selfHarm && !harmOthers) return { detected: false, type: null };
  return { detected: true, type: selfHarm && harmOthers ? 'both' : selfHarm ? 'self' : 'others' };
};

const sanitizeInput = (text) => {
  let cleaned = text;
  cleaned = cleaned.replace(/(?:ignore|forget|disregard|override)\s+(?:all\s+)?(?:previous|prior|above|your)\s+(?:instructions|rules|prompt|guidelines)/gi, '[removed]');
  cleaned = cleaned.replace(/(?:you\s+are\s+now|pretend\s+(?:you\s+are|to\s+be)|act\s+as\s+(?:if\s+you\s+are|a))\s/gi, '[removed] ');
  cleaned = cleaned.replace(/(?:system|developer|admin)\s*(?:prompt|message|override|mode)\s*[:=]/gi, '[removed]:');
  return cleaned;
};

// ── Component ─────────────────────────────────────────────────
export default function SayItYourWayCommunication({ isOpen, onClose, onExtracted, conflictContext, previousStatements, trigger }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extractedStatement, setExtractedStatement] = useState(null);
  const [crisisDetected, setCrisisDetected] = useState(null);
  const [emotionalTone, setEmotionalTone] = useState({ level: 0, label: "Neutral", color: "stone" });
  const [isRecording, setIsRecording] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const recognitionRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const extractedRef = useRef(false);

  // Scroll chat container to bottom (without affecting page scroll)
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isLoading]);

  // Update tone
  useEffect(() => {
    setEmotionalTone(analyzeEmotionalTone(messages));
  }, [messages]);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    extractedRef.current = false;
    setExtractedStatement(null);
    setError(null);
    setInput("");
    setCrisisDetected(null);
    setIsCollapsed(false);
    setEmotionalTone({ level: 0, label: "Neutral", color: "stone" });
    setMessages([{ role: "assistant", content: GREETING }]);
  }, [isOpen]);

  // Notify parent and auto-collapse when statement is ready
  useEffect(() => {
    if (extractedStatement && onExtracted) {
      extractedRef.current = true;
      onExtracted(extractedStatement);
      // Auto-collapse so user can see the Because field and submit button
      setIsCollapsed(true);
    }
  }, [extractedStatement]);

  // Voice input
  const toggleVoiceInput = () => {
    if (isRecording) { recognitionRef.current?.stop(); setIsRecording(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError("Voice input not supported. Try Chrome or Edge."); return; }
    const recognition = new SR();
    recognition.continuous = false; recognition.interimResults = false; recognition.lang = "en-US";
    recognition.onresult = (e) => { setInput((prev) => prev + (prev ? " " : "") + e.results[0][0].transcript); setIsRecording(false); };
    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  // Send message
  const sendMessage = async (userMessage) => {
    if (!userMessage.trim()) return;
    const sanitized = sanitizeInput(userMessage);

    // Harm detection
    const harmCheck = detectHarmClientSide(sanitized);
    if (harmCheck.detected) {
      setCrisisDetected(harmCheck.type);
      setMessages((prev) => [...prev,
        { role: "user", content: sanitized },
        { role: "assistant", content: harmCheck.type === 'self' || harmCheck.type === 'both'
          ? "I want to make sure you're okay. If you're having thoughts of hurting yourself, please reach out:\n\n📞 988 Suicide & Crisis Lifeline: Call or text 988\n💬 Crisis Text Line: Text HELLO to 741741\n🚨 Emergency: Call 911\n\nWhen you're ready to continue, I'm here."
          : "I'm not able to continue as someone may be at risk. If there's immediate danger, please contact 911."
        }
      ]);
      setInput("");
      inputRef.current?.focus();
      return;
    }

    const newMessages = [...messages, { role: "user", content: sanitized }];
    setMessages(newMessages);
    setInput("");
    inputRef.current?.focus();
    setIsLoading(true);
    setError(null);
    // Scroll SIYW panel top into view so user sees the full conversation
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    try {
      const token = await window.firebaseAuth?.currentUser?.getIdToken(false);
      const response = await fetch(CLOUD_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
          mode: 'because',
          conflictContext: conflictContext || '',
          previousStatements: previousStatements || '',
        }),
      });

      if (response.status === 429) {
        setError("Too many messages. Please wait a moment.");
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Server error (${response.status})`);
      }

      const data = await response.json();
      const assistantText = data.content?.map((c) => c.text || "").join("") || "Could you try explaining that again?";

      const rawExtracted = extractJSON(assistantText);
      if (rawExtracted?.extracted && rawExtracted?.becauseStatement) {
        const cleaned = sanitizeBecauseOutput(rawExtracted.becauseStatement);
        setExtractedStatement(cleaned);
      }

      setMessages([...newMessages, { role: "assistant", content: assistantText }]);
    } catch (err) {
      setError(err.message || "Failed to get a response. Please try again.");
    }
    setIsLoading(false);
    // Focus input and keep panel top visible after response arrives
    setTimeout(() => {
      inputRef.current?.focus();
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  // Tone colors
  const toneColors = {
    red: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", fill: "bg-red-500" },
    orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", fill: "bg-orange-500" },
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", fill: "bg-amber-500" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", fill: "bg-emerald-500" },
    green: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", fill: "bg-green-500" },
    stone: { bg: "bg-stone-50", border: "border-stone-200", text: "text-stone-600", fill: "bg-stone-400" },
  };
  const tc = toneColors[emotionalTone.color] || toneColors.stone;

  if (!isOpen) return null;

  return (
    <div ref={panelRef} className="mb-4 border-2 border-purple-200 rounded-xl overflow-hidden bg-white shadow-lg">
      {/* Header — always visible, clickable to collapse/expand */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-white font-bold text-sm">Say It Your Way</h3>
            <p className="text-purple-50 text-[10px]">Vent freely. I'll help shape your Because statement</p>
          </div>
          {trigger && (
            <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full ml-2">
              {trigger === 'deletions' ? 'Struggling detected' : trigger === 'time' ? 'Taking a while' : 'After validation'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          {isCollapsed ? <ChevronDown className="w-4 h-4 text-white" /> : <ChevronUp className="w-4 h-4 text-white" />}
        </div>
      </button>

      {!isCollapsed && (
        <>
          {/* Emotional Tone */}
          {messages.filter((m) => m.role === "user").length > 0 && (
            <div className={`px-4 py-2 ${tc.bg} border-b ${tc.border} flex items-center gap-3`}>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] font-semibold ${tc.text} uppercase tracking-wider`}>
                    Tone: {emotionalTone.label}
                  </span>
                  <span className={`text-[10px] ${tc.text}`}>{emotionalTone.level}%</span>
                </div>
                <div className="h-1 bg-white/60 rounded-full overflow-hidden">
                  <div className={`h-full ${tc.fill} rounded-full transition-all duration-1000`} style={{ width: `${emotionalTone.level}%` }} />
                </div>
              </div>
              {emotionalTone.level <= 35 && messages.filter((m) => m.role === "user").length > 2 && (
                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">🌿 Calming</span>
              )}
            </div>
          )}

          {/* Chat Messages */}
          <div ref={chatContainerRef} className="max-h-64 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                  msg.role === "user" ? "bg-purple-600 text-white rounded-br-md" : "bg-stone-100 text-stone-800 rounded-bl-md"
                }`}>
                  {msg.role === "assistant" ? cleanDisplayText(msg.content) : msg.content}
                  {msg.role === "assistant" && (
                    <div className="mt-1 flex justify-end">
                      <AiContentReportButton
                        content={msg.content}
                        context={messages.slice(Math.max(0, idx - 2), idx)}
                        source="siyw-communication"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-stone-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="text-center">
                <span className="text-xs text-red-700 bg-red-50 px-3 py-1 rounded-full">⚠️ {error}</span>
              </div>
            )}

            {/* Extraction confirmation */}
            {extractedStatement && (
              <div className="p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
                <p className="text-sm font-semibold text-green-800 mb-1">✅ Got it! Your statement is ready.</p>
                <div className="text-xs text-green-700 bg-white/60 rounded-lg p-2 mb-2 break-words italic">
                  "{extractedStatement}"
                </div>
                <p className="text-[10px] text-green-700">
                  This has been filled into the Because statement field above. Edit it to make it your own (at least 10 character changes required).
                </p>
              </div>
            )}

          </div>

          {/* Input */}
          <div className="border-t border-stone-200 px-3 py-2 bg-white">
            <div className="flex items-end gap-2">
              <button
                onClick={toggleVoiceInput}
                className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                  isRecording ? "bg-red-600 text-white shadow-lg animate-pulse" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
                title={isRecording ? "Stop recording" : "Voice input"}
              >
                {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>

              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                placeholder={isRecording ? "🎙️ Listening..." : "Vent away, no filter needed..."}
                rows={1}
                disabled={isLoading}
                style={{ minHeight: "38px", maxHeight: "80px" }}
              />

              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                className="p-2 bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 text-white rounded-lg transition-all flex-shrink-0 active:scale-95"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[9px] text-stone-500">Enter to send · Shift+Enter for new line</span>
              {isRecording && <span className="text-[9px] text-red-700 font-medium animate-pulse">● Recording...</span>}
            </div>
            <div className="mt-1 pt-1 border-t border-stone-100">
              <p className="text-[8px] text-stone-500 text-center">
                Not therapy or counseling. In crisis? Call <a href="tel:988" className="underline">988</a> or <a href="tel:911" className="underline">911</a>.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Crisis Modal */}
      {crisisDetected && (
        <ModalDialog onClose={() => setCrisisDetected(null)} labelledBy="siyw-comm-crisis-title" className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4" onClick={() => setCrisisDetected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-t-2xl px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"><Heart className="w-7 h-7 text-white" /></div>
                <div>
                  <h2 id="siyw-comm-crisis-title" className="text-xl font-bold text-white">We Care About You</h2>
                  <p className="text-white text-sm">You are not alone. Help is available.</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {(crisisDetected === 'self' || crisisDetected === 'both') && (
                <div className="space-y-3">
                  <p className="text-stone-700 text-sm">We noticed language that may indicate you're going through a very difficult time.</p>
                  <a href="tel:988" className="flex items-center gap-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0"><Phone className="w-6 h-6 text-white" /></div>
                    <div><p className="font-bold text-red-800 text-lg">988 Suicide & Crisis Lifeline</p><p className="text-red-700 text-sm">Call or text <strong>988</strong> (24/7)</p></div>
                  </a>
                  <a href="sms:741741&body=HELLO" className="flex items-center gap-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0"><MessageSquare className="w-6 h-6 text-white" /></div>
                    <div><p className="font-bold text-blue-800 text-lg">Crisis Text Line</p><p className="text-blue-600 text-sm">Text <strong>HELLO</strong> to <strong>741741</strong></p></div>
                  </a>
                </div>
              )}
              {(crisisDetected === 'others' || crisisDetected === 'both') && (
                <a href="tel:911" className="flex items-center gap-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0"><Phone className="w-6 h-6 text-white" /></div>
                  <div><p className="font-bold text-red-800 text-lg">Emergency: 911</p><p className="text-red-700 text-sm">Call if anyone is in immediate danger</p></div>
                </a>
              )}
              <button onClick={() => setCrisisDetected(null)} className="w-full py-3 bg-stone-800 hover:bg-stone-900 text-white rounded-xl font-medium">I Understand</button>
            </div>
          </div>
        </ModalDialog>
      )}
    </div>
  );
}
